<template>
  <div style="margin-bottom: 150px">
    <div class="py-4 d-flex align-center flex-wrap" style="gap: 10px">
      <v-text-field
        label="Search sources"
        v-model="search"
        clearable
        flat
        hide-details
        variant="solo"
        bg-color="smokeDark"
        density="compact"
        rounded="lg"
        style="border-radius: 6px; max-width: 300px"
        prepend-inner-icon="mdi-magnify"
      ></v-text-field>
      <v-select
        label="Filter by source"
        :items="sourceItems"
        item-title="name"
        item-value="uid"
        v-model="source"
        clearable
        v-if="sourceItems.length > 0"
        hide-details
        flat
        density="compact"
        variant="solo"
        bg-color="smokeDark"
        rounded="lg"
        style="border-radius: 6px; max-width: 200px"
      ></v-select>
      <v-btn @click="geminiSearch">Gemini Search</v-btn>
    </div>

    <div class="mb-8" v-if="loading">
      <v-progress-circular
        indeterminate
        color="accent"
        width="2"
      ></v-progress-circular>
    </div>

    <!-- Source Cards -->
    <div
      class="d-flex flex-column mt-4"
      style="gap: 16px"
      v-if="allSources.length > 0"
    >
      <v-card
        v-for="sourceItem in allSources"
        :key="sourceItem.uid"
        elevation="0"
        style="border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 8px"
      >
        <div class="d-flex">
          <!-- Image Section -->
          <div style="width: 192px; flex-shrink: 0; background-color: #f1f5f9">
            <v-img
              v-if="sourceItem.image"
              :src="sourceItem.image"
              cover
              style="width: 100%; height: 100%; min-height: 192px"
            ></v-img>
            <div
              v-else
              class="d-flex align-center justify-center"
              style="width: 100%; height: 192px; background-color: #f1f5f9"
            >
              <v-icon size="48" color="grey">mdi-image-off</v-icon>
            </div>
          </div>

          <!-- Content Section -->
          <div class="flex-grow-1 pa-5">
            <div class="d-flex align-start justify-space-between mb-2">
              <div class="flex-grow-1">
                <h3 class="text-h6 font-weight-semibold mb-1">
                  {{ sourceItem.name }}
                </h3>
                <a
                  v-if="sourceItem.url"
                  :href="sourceItem.url"
                  target="_blank"
                  class="text-body-2 text-medium-emphasis text-decoration-none d-inline-flex align-center"
                  style="gap: 4px"
                  @click.stop
                >
                  <v-icon size="14">mdi-open-in-new</v-icon>
                  {{ formatUrl(sourceItem.url) }}
                </a>
              </div>

              <!-- Action Menu -->
              <v-menu offset-y>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    v-bind="props"
                    class="text-medium-emphasis"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="refreshSource(sourceItem)">
                    <template v-slot:prepend>
                      <v-icon>mdi-pencil</v-icon>
                    </template>
                    <v-list-item-title>Fetch latest</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteSource(sourceItem)">
                    <template v-slot:prepend>
                      <v-icon>mdi-delete</v-icon>
                    </template>
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <!-- Metadata -->
            <div class="d-flex align-center mt-4" style="gap: 16px">
              <div
                v-if="sourceItem.created_by_email"
                class="d-flex align-center text-caption text-medium-emphasis"
                style="gap: 6px"
              >
                <v-icon size="14">mdi-account</v-icon>
                <span>{{ sourceItem.created_by_email }}</span>
              </div>
              <div
                v-if="sourceItem.created_at"
                class="d-flex align-center text-caption text-medium-emphasis"
                style="gap: 6px"
              >
                <v-icon size="14">mdi-clock-outline</v-icon>
                <span
                  >Last fetched {{ formatDate(sourceItem.last_fetch) }}</span
                >
              </div>
            </div>

            <!-- Category Badge -->
            <div class="mt-3">
              <v-chip
                v-if="getCategoryName(sourceItem.category)"
                size="small"
                variant="flat"
                color="grey-lighten-4"
                class="text-grey-darken-1"
              >
                {{ getCategoryName(sourceItem.category) }}
              </v-chip>
              <v-chip
                v-else
                size="small"
                variant="flat"
                color="grey-lighten-4"
                class="text-grey-darken-1"
              >
                Uncategorized
              </v-chip>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <div
      class="d-flex flex-column"
      style="gap: 15px"
      v-if="filteredItems.length > 0"
    >
      <v-card
        v-for="i in filteredItems"
        :key="i.uid"
        flat
        style="
          border-left: 1px solid #272727;
          border-right: 3px solid #272727;
          border-top: 1px solid #272727;
          border-bottom: 3px solid #272727;
          border-radius: 6px;
        "
        max-width="1200"
      >
        <div
          style="
            display: grid;
            grid-template-columns: 150px 140px minmax(0, 1fr) 250px;
          "
        >
          <div style="width: 150px">
            <v-img cover :src="i.source.image" width="150" />
          </div>
          <div
            class="d-flex flex-column align-center justify-center bg-smokeDark"
          >
            <p style="font-size: 22pt; font-weight: 600">{{ i.match }}</p>
            <p style="font-size: 10pt; color: #666">Match score</p>
          </div>
          <div class="pl-4 d-flex py-4 flex-column justify-center">
            <p
              style="
                font-size: 13pt;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                display: block;
              "
              :title="i.title"
            >
              {{ i.title }}
            </p>
            <p
              style="
                font-size: 10pt;
                color: #666;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                display: block;
              "
            >
              Sourced from {{ i.source.name }}
              <u
                class="mx-1"
                style="cursor: pointer"
                @click="openLink(i.url)"
                >{{ i.url }}</u
              >
            </p>
          </div>
          <div class="d-flex align-center pa-4" style="gap: 20px">
            <v-btn
              size="small"
              variant="text"
              @click="$store.dispatch('refreshSource', i.source)"
              >Fetch latest</v-btn
            >
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { post } from "@/mixins/post";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";

export default {
  mixins: [post],
  data() {
    return {
      sourceName: "",
      sourceUrl: "",
      source: null,
      search: "",
      item_loading: null,
      sourceMenu: false,
    };
  },
  methods: {
    async geminiSearch() {
      const geminiSearch = httpsCallable(functions, "geminiSearch");
      const result = await geminiSearch({
        brand: this.brand,
      });
      console.log(result.data);
    },
    refreshSource(sourceItem) {
      this.$store.dispatch("refreshSource", sourceItem);
    },
    addSource() {
      this.$store.dispatch("saveSource", {
        url: this.sourceUrl,
        name: this.sourceName,
        last_fetch: null,
        category: null,
      });
    },
    async refreshSources() {
      this.$store.commit("setLoading", { type: "sources", val: true });
      await Promise.all(
        this.filteredSources.map(async (s) => {
          console.log("refreshing source", s);
          await this.$store.dispatch("refreshSource", s);
        })
      );
      console.log("sources refreshed");
      this.$store.commit("setNotification", {
        show: true,
        text: "Sources refreshed!",
        type: "primary",
        icon: "",
      });
      this.$store.commit("setLoading", { type: "sources", val: false });
    },
    isItemLoading(item) {
      return this.item_loading === item.uid;
    },
    openLink(url) {
      window.open(url, "_blank");
    },
    async turnIntoPost(item) {
      console.log("turning into post", item);
      this.item_loading = item.uid;

      this.media = [await this.getArticlePreview(item.url)];
      this.category = this.categories.find(
        (c) => c.uid === this.query.category
      );
      this.sheet = true;
      this.item_loading = null;
    },
    async getArticlePreview(url) {
      let prefix = "https://";
      let link = url;
      if (url != "") {
        if (!link.includes("http")) {
          link = prefix.concat(url);
        }

        console.log("Getting link preview");
        const getPreview = httpsCallable(functions, "getLinkPreview");
        const res = await getPreview({
          url: link,
        });
        console.log(res.data);
        let v = res.data;
        return {
          alt: v.images,
          type: "url",
          url: v.url,
          src: v.image,
          description: v.description,
          language: v.language,
          title: v.title,
        };
      }
      return null;
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatUrl(url) {
      if (!url) return "";
      try {
        const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
        return urlObj.hostname.replace("www.", "");
      } catch {
        return url;
      }
    },
    getCategoryName(categoryUid) {
      if (!categoryUid) return null;
      const category = this.categories.find((c) => c.uid === categoryUid);
      return category ? category.name : null;
    },
    editSource(sourceItem) {
      // Add edit functionality here
      console.log("Edit source", sourceItem);
    },
    deleteSource(sourceItem) {
      if (sourceItem) {
        this.$store.dispatch("deleteSource", sourceItem);
      }
    },
  },
  computed: {
    ...mapState({
      sources: (state) => state.sources.sources,
      allSources: (state) => state.sources.allSources,
      query: (state) => state.posts.query,
      categories: (state) => state.categories.categories,
      loading: (state) => state.loading.sources,
      brand: (state) => state.workspace.brand,
    }),

    filteredSources() {
      let filtered = this.allSources;
      if (this.search) {
        filtered = filtered.filter((s) =>
          s.title?.toLowerCase().includes(this.search.toLowerCase())
        );
      }

      return filtered;
    },
    filteredItems() {
      let items = [];
      if (this.query.category && this.sources[this.query.category]) {
        console.log("sources", this.sources[this.query.category]);
        this.sources[this.query.category].map((s) => {
          let source = { ...s };
          delete source.items;
          items = items.concat(s.items.map((i) => ({ ...i, source: source })));
        });
      }

      if (this.search) {
        items = items.filter((i) =>
          i.title?.toLowerCase().includes(this.search.toLowerCase())
        );
      }
      console.log("items", items);
      return items;
    },
    sourceItems() {
      let items = [];
      if (this.query.category && this.sources[this.query.category]) {
        this.sources[this.query.category].map((s) => {
          items.push({
            url: s.url,
            uid: s.uid,
            name: s.name,
          });
        });
      }

      console.log("source items", items);
      return items;
    },
  },
};
</script>
